import PieChart from 'lib/Charts/PieChart';
import classNames from 'classnames/bind';
import EmptyPieChart from 'components/common/HeroCharts/EmptyPieChart';
import { formatNumber } from 'modules/I18N';
import { useSelector } from 'react-redux';
import { getLocaleCode } from 'store/publisher/selectors';
import styles from './BidsRenderer.pcss';
import { BidsRendererProps } from './BidsRenderer.types';
import { getChartData, getChartOptions, getNetValue } from './utils/utils';

const cx = classNames.bind(styles);

const BidsRenderer: React.FC<BidsRendererProps> = ({ value = {}, data = {}, onClick = () => undefined }) => {
  const localeCode = useSelector(getLocaleCode);

  const onPieChartClick = (): void => onClick(data);

  if (!value.bidRequest && !value.bidResponse) {
    return <EmptyPieChart onChartClick={onPieChartClick} />;
  }

  const ctPieChartColor = ['#2ee49b', '#6e2ee4'];

  const chartData = getChartData({ bidRequest: value.bidRequest, bidResponse: value.bidResponse });
  const chartNetValue = getNetValue({
    bidRequest: value.bidRequest,
    bidResponse: value.bidResponse,
    wonImpressions: value.wonImpressions,
    localeCode,
  });
  const chartOptions = getChartOptions();

  return (
    <div className="relative flex items-center body-sm">
      <PieChart
        colors={ctPieChartColor}
        data={chartData}
        options={chartOptions}
        hasDataLabel={false}
        hasEnabledExporting={false}
        hasTooltip={false}
        onClick={onPieChartClick}
        dataTestId="BidsRenderer-pieChart"
        chartHeight="60px"
        chartWidth="60px"
        chartSize="70%"
      />
      <div className={cx('chartLegend')} data-test-id="BidsRenderer-chartLegend">
        <div className="mb-1">
          <span className={cx('legends-color')} style={{ backgroundColor: ctPieChartColor[0] }} />
          {formatNumber({ value: value.bidRequest ?? 0, localeCode })}
          &nbsp;{chartData[0].name}
        </div>
        <div>
          <span className={cx('legends-color')} style={{ backgroundColor: ctPieChartColor[1] }} />
          {formatNumber({ value: value.bidResponse ?? 0, localeCode })}
          &nbsp;{chartData[1].name}
        </div>
      </div>
      <div className={cx('chartDetail')} data-test-id="BidsRenderer-chartDetail">
        <div className="mb-1">
          {formatNumber({ value: value.wonImpressions ?? 0, localeCode })}
          &nbsp;Won
        </div>
        <div>
          {chartNetValue}
          &nbsp;Traded win rate
        </div>
      </div>
    </div>
  );
};

export default BidsRenderer;
